import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import gsap from 'gsap';

// Pantalla de carga "IA trabajando" reutilizable. Clip en loop (Remotion) +
// progreso + lista de pasos que avanza, todo con entrada/realce animados por
// GSAP. Paleta dark fija (matchea el clip), independiente del theme claro de la
// app. Se usa en: análisis de marca, enriquecer fuentes, generación, onboarding.

const LOOP_SRC = '/loaders/ai-processing.mp4';
const STEP_MS = 1200;

export interface AiLoadingOverlayProps {
  open: boolean;
  title: string;
  subtitle?: string;
  steps: string[];
  /** 0..100 → barra determinada. Omitido → barra indeterminada (shimmer). */
  progress?: number;
  /** 'overlay' = fixed con backdrop (bloquea); 'inline' = card en el flujo. */
  variant?: 'overlay' | 'inline';
  kicker?: string;
  onCancel?: () => void;
  cancelLabel?: string;
}

export function AiLoadingOverlay({
  open, title, subtitle, steps, progress, variant = 'overlay', kicker = 'AgencySMM', onCancel, cancelLabel = 'Cancel',
}: AiLoadingOverlayProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  // Avance decorativo de los pasos mientras está abierto.
  useEffect(() => {
    if (!open || steps.length === 0) return;
    setActive(0);
    const id = window.setInterval(() => setActive((a) => (a + 1) % steps.length), STEP_MS);
    return () => window.clearInterval(id);
  }, [open, steps.length]);

  // Entrada con GSAP (panel + glow del clip).
  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(panelRef.current, { opacity: 0, y: 26, scale: 0.96, duration: 0.5, ease: 'power3.out' });
      gsap.from('[data-frame]', { opacity: 0, scale: 0.92, duration: 0.7, ease: 'power2.out', delay: 0.05 });
      gsap.from('[data-step]', { opacity: 0, x: -12, duration: 0.4, stagger: 0.06, delay: 0.2, ease: 'power2.out' });
    }, rootRef);
    return () => ctx.revert();
  }, [open]);

  // Pop del marcador activo en cada cambio.
  useEffect(() => {
    if (!open) return;
    const mark = rootRef.current?.querySelector(`[data-step="${active}"] [data-mark]`);
    if (mark) gsap.fromTo(mark, { scale: 0.55 }, { scale: 1, duration: 0.45, ease: 'back.out(2.2)' });
  }, [active, open]);

  if (!open) return null;

  const pct = typeof progress === 'number' ? Math.max(0, Math.min(100, Math.round(progress))) : null;

  const panel = (
    <Panel ref={panelRef} $inline={variant === 'inline'}>
      <Frame data-frame>
        <video src={LOOP_SRC} autoPlay muted loop playsInline />
        <FrameGlow />
      </Frame>
      <Kicker>● {kicker}</Kicker>
      <Title>{title}</Title>
      {subtitle && <Sub>{subtitle}</Sub>}

      <Bar>
        {pct === null ? <BarIndeterminate /> : <BarFill style={{ width: `${Math.max(pct, 4)}%` }} />}
      </Bar>
      {pct !== null && <Pct>{pct}%</Pct>}

      {steps.length > 0 && (
        <Steps>
          {steps.map((s, i) => {
            const state = i < active ? 'done' : i === active ? 'active' : 'pending';
            return (
              <Step key={s} data-step={i} $state={state}>
                <Mark data-mark $state={state}>{state === 'done' ? '✓' : ''}</Mark>
                {s}
              </Step>
            );
          })}
        </Steps>
      )}

      {onCancel && (
        <CancelBtn type="button" onClick={onCancel}>{cancelLabel}</CancelBtn>
      )}
    </Panel>
  );

  if (variant === 'inline') {
    return <InlineWrap ref={rootRef}>{panel}</InlineWrap>;
  }
  return <Backdrop ref={rootRef}>{panel}</Backdrop>;
}

// ─── Paleta dark fija (matchea el clip) ──────────────────────────────────────
const C = {
  ink: '#0A0B16',
  panel: '#0E1230',
  line: '#262A52',
  paper: '#EEF1FF',
  muted: '#9AA3C7',
  cyan: '#22D3EE',
  blue: '#3B82F6',
  violet: '#8B5CF6',
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(8, 9, 20, 0.72);
  backdrop-filter: blur(6px);
`;

const InlineWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 24px 0;
`;

const Panel = styled.div<{ $inline: boolean }>`
  width: 100%;
  max-width: 560px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px;
  border-radius: 20px;
  background: linear-gradient(160deg, ${C.panel}, ${C.ink});
  border: 1px solid ${C.line};
  box-shadow: ${({ $inline }) => ($inline ? '0 20px 50px rgba(0,0,0,0.25)' : '0 40px 100px rgba(0,0,0,0.6)')};
  color: ${C.paper};
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${C.line};
  margin-bottom: 22px;

  video { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const FrameGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 0 0 60px rgba(99, 102, 241, 0.25);
`;

const Kicker = styled.div`
  font-size: 12px;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${C.cyan};
  font-weight: 700;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: clamp(22px, 4vw, 30px);
  font-weight: 800;
  margin: 0 0 8px;
  color: ${C.paper};
`;

const Sub = styled.p`
  font-size: 14px;
  color: ${C.muted};
  margin: 0 0 22px;
  max-width: 44ch;
  line-height: 1.5;
`;

const Bar = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  height: 6px;
  border-radius: 999px;
  background: ${C.line};
  overflow: hidden;
  margin-top: 4px;
`;

const BarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${C.cyan}, ${C.violet});
  border-radius: 999px;
  transition: width 0.6s ease;
`;

const shimmer = keyframes`
  0% { left: -40%; }
  100% { left: 100%; }
`;

const BarIndeterminate = styled.div`
  position: absolute;
  top: 0;
  left: -40%;
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, transparent, ${C.cyan}, ${C.violet}, transparent);
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Pct = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  color: ${C.cyan};
  margin-top: 10px;
`;

const Steps = styled.ul`
  width: 100%;
  max-width: 440px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
`;

const stepPulse = keyframes`0%,100% { opacity: 1; } 50% { opacity: 0.55; }`;

const Step = styled.li<{ $state: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${({ $state }) => ($state === 'pending' ? C.muted : C.paper)};
  animation: ${({ $state }) => ($state === 'active' ? stepPulse : 'none')} 1.2s ease infinite;
`;

const Mark = styled.span<{ $state: string }>`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: ${C.ink};
  background: ${({ $state }) => ($state === 'done' ? C.cyan : $state === 'active' ? C.violet : 'transparent')};
  border: 1px solid ${({ $state }) => ($state === 'pending' ? C.line : 'transparent')};
`;

const CancelBtn = styled.button`
  margin-top: 24px;
  padding: 8px 18px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid ${C.line};
  color: ${C.muted};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover { color: ${C.paper}; border-color: ${C.muted}; }
`;
